import type {
  BlockStimulus,
  StimulusIconKey,
  StimulusTheme,
  StimulusType,
} from '../types/payloads/stimulus.types';

const LETTER_SALUTATION_RE = /\b(querid[oa]|estimad[oa]|apreciad[oa])\s+[\wáéíóúñ]/i;
const LETTER_CLOSING_RE =
  /\b(atentamente|saludos cordiales|de mi consideraci[oó]n|con cariño|con afecto)\b/i;
const LETTER_DATE_RE =
  /^(?:[A-ZÁÉÍÓÚÑ][\wáéíóúñ\s,.-]+,\s*)?\d{1,2}\s+de\s+[a-záéíóúñ]+\s+de\s+\d{4}/i;

const ICON_RULES: Array<{ key: StimulusIconKey; re: RegExp }> = [
  { key: 'sport', re: /\b(f[uú]tbol|deporte|gol|partido|estadio|ol[ií]mpic|atleta|basquetbol|tenis)\b/i },
  { key: 'science', re: /\b(experimento|laboratorio|ciencia|hip[oó]tesis|mol[eé]cula|átomo|fotos[ií]ntesis)\b/i },
  { key: 'history', re: /\b(siglo|guerra|revoluci[oó]n|independencia|imperio|colonia|treaty|tratado|decreto)\b/i },
  { key: 'nature', re: /\b(r[ií]o|monta[nñ]a|bosque|oc[eé]ano|fauna|flora|clima|ecosistema|valle)\b/i },
];

function looksLikeTheoreticalGenreText(blob: string): boolean {
  return /\b(?:toda|todo|toda la)\s+(?:carta|noticia|nota|poema)\b|\b(?:sigue|presenta)\s+(?:una\s+)?estructura\b|\b(?:se\s+compone|elementos?\s+de\s+una?)\b/i.test(
    blob,
  );
}

function looksLikeLetterSample(content: string): boolean {
  const blob = content.trim();
  if (!blob || looksLikeTheoreticalGenreText(blob)) return false;
  const firstLine = blob.split('\n')[0]?.trim() ?? '';
  return (
    LETTER_SALUTATION_RE.test(blob) ||
    LETTER_CLOSING_RE.test(blob) ||
    LETTER_DATE_RE.test(firstLine) ||
    /\b(te escribo|le escribo)\b/i.test(blob)
  );
}

export function inferStimulusTheme(
  type: StimulusType,
  content: string,
  title = '',
): StimulusTheme {
  const blob = `${title} ${content}`;

  if (
    type === 'news' && !looksLikeTheoreticalGenreText(blob)
  ) {
    return 'newspaper';
  }
  if (
    !looksLikeTheoreticalGenreText(blob) &&
    /\b(ep[ií]grafe|titular|bajada|lead)\s*:/i.test(blob) &&
    blob.length >= 40
  ) {
    return 'newspaper';
  }
  if (
    !looksLikeTheoreticalGenreText(blob) &&
    /\b(titular|lead|bajada|reportaje)\b/i.test(blob) &&
    /\b(ayer|hoy|ocurrió|afectó|informó)\b/i.test(blob)
  ) {
    return 'newspaper';
  }

  if (type === 'poem' && !looksLikeTheoreticalGenreText(blob)) {
    return 'poem';
  }

  if (looksLikeLetterSample(content)) {
    return 'letter';
  }

  if (
    type === 'historical_source' ||
    type === 'scientific_data' ||
    /\b(documento|fuente|archivo|testimonio|c[eé]dula|manuscrito|acta)\b/i.test(blob)
  ) {
    return 'document';
  }
  return 'prose';
}

export function inferStimulusIconKey(content: string, title = ''): StimulusIconKey {
  const blob = `${title} ${content}`;
  for (const rule of ICON_RULES) {
    if (rule.re.test(blob)) return rule.key;
  }
  return 'general';
}

export function resolveStimulusTheme(stimulus: BlockStimulus): StimulusTheme {
  if (stimulus.theme) return stimulus.theme;
  return inferStimulusTheme(stimulus.type, stimulus.content, stimulus.title ?? '');
}

export function splitLetterContent(content: string): { dateLine?: string; body: string } {
  const lines = content.split('\n');
  const first = lines[0]?.trim() ?? '';
  if (LETTER_DATE_RE.test(first) || /^[\wáéíóúñ\s,.-]+,\s+\d/.test(first)) {
    return { dateLine: first, body: lines.slice(1).join('\n').trim() || content };
  }
  return { body: content };
}

export function shouldUseNewspaperColumns(content: string): boolean {
  return content.trim().length >= 280;
}
